import { useCallback } from "react"

import { useOrganization, useUser } from "@clerk/nextjs"
import { usePostHog } from "posthog-js/react"

import { logger } from "@/lib/logger"

import { safePostHogProperties } from "./safePostHogProperties"
import type {
  AnalyticsProperties,
  EventPropertyMap,
  TrackingEventName,
} from "./types"

const log = logger("Analytics")

export type { AnalyticsProperties } from "./types"

/** Known PostHog group types. */
type AnalyticsGroupType = "organization" | "workspace"

interface TrackEventOptions {
  includeIdentity?: boolean
}

export const useAnalytics = () => {
  const posthog = usePostHog()
  const { organization } = useOrganization()
  const { user } = useUser()

  const trackEvent = useCallback(
    <E extends TrackingEventName>(
      eventName: E,
      additionalProperties?: EventPropertyMap[E],
      options?: TrackEventOptions,
    ) => {
      if (!posthog) {
        log.warn("PostHog is not initialized")
        return
      }

      const defaultProperties =
        options?.includeIdentity === false
          ? {}
          : {
              organization_name: organization?.name || "Unknown Organization",
              organization_id: organization?.id || "Unknown ID",
              user_firstname: user?.firstName || user?.username || "Anonymous",
              user_id: user?.id || "Unknown User ID",
            }

      const properties = {
        ...defaultProperties,
        ...additionalProperties,
      }

      posthog.capture(eventName, safePostHogProperties(properties))
    },
    [
      posthog,
      organization?.name,
      organization?.id,
      user?.firstName,
      user?.username,
      user?.id,
    ],
  )

  /**
   * Associate the current distinct id with a known user. Centralized here so
   * `posthog.identify` is never called directly outside the analytics module.
   */
  const identify = useCallback(
    (distinctId: string, properties?: AnalyticsProperties) => {
      if (!posthog) {
        log.warn("PostHog is not initialized")
        return
      }
      posthog.identify(distinctId, properties)
    },
    [posthog],
  )

  /**
   * Associate subsequent events with a group (e.g. the org). Centralized here
   * so `posthog.group` is never called directly outside the analytics module.
   */
  const group = useCallback(
    (
      groupType: AnalyticsGroupType,
      groupKey: string,
      properties?: AnalyticsProperties,
    ) => {
      if (!posthog) {
        log.warn("PostHog is not initialized")
        return
      }
      posthog.group(groupType, groupKey, properties)
    },
    [posthog],
  )

  return { trackEvent, identify, group, isReady: Boolean(posthog) }
}
